// TOEFL iBT Mock 2 — Writing (2026 format, intermediate difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets a
//                       common structure (present perfect, comparatives,
//                       "used to", gerund subjects, "too … to", indirect
//                       questions). `accept` lists other word orders that
//                       are also grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal complaint about a repair request
//                       that has been ignored.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       on later school start times AND name a group it
//                       could cause problems for and what they should get.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Have you ever been to the science museum?",
    prefix: "Yes,",
    answer: ["I", "have", "been", "there", "twice", "with", "my", "family"],
    suffix: ".",
    distractors: ["went", "was"],
  },
  {
    id: 2,
    speaker: "Is the new cafeteria better than the old one?",
    prefix: "Definitely. The food is",
    answer: ["much", "fresher", "and", "the", "prices", "are", "lower"],
    suffix: ".",
    distractors: ["more", "low"],
  },
  {
    id: 3,
    speaker: "Do you still play the piano?",
    prefix: "Not anymore, but",
    answer: ["I", "used", "to", "practice", "every", "day", "as", "a", "child"],
    accept: [["as", "a", "child", "I", "used", "to", "practice", "every", "day"]],
    suffix: ".",
    distractors: ["use", "practicing"],
  },
  {
    id: 4,
    speaker: "What's the best way to improve my English?",
    prefix: "In my experience,",
    answer: ["reading", "every", "day", "is", "the", "most", "helpful", "habit"],
    suffix: ".",
    distractors: ["read", "more"],
  },
  {
    id: 5,
    speaker: "Why didn't you go to the concert last night?",
    prefix: "I was",
    answer: ["too", "tired", "to", "go", "out", "after", "work"],
    suffix: ".",
    distractors: ["enough", "going"],
  },
  {
    id: 6,
    speaker: "Can you tell me where the registrar's office is?",
    prefix: "I'm not sure, but",
    answer: ["I", "think", "it", "is", "next", "to", "the", "library"],
    suffix: ".",
    distractors: ["thinking", "near"],
  },
  {
    id: 7,
    speaker: "What did the professor say about the essay?",
    prefix: "She asked",
    answer: ["us", "to", "submit", "it", "by", "Friday", "afternoon"],
    suffix: ".",
    distractors: ["submitting", "until"],
  },
  {
    id: 8,
    speaker: "Do you know when the next bus leaves?",
    prefix: "Let me check.",
    answer: ["It", "leaves", "in", "about", "ten", "minutes"],
    suffix: ".",
    distractors: ["leaving", "on"],
  },
  {
    id: 9,
    speaker: "Why are you learning Spanish?",
    prefix: "Because",
    answer: ["I", "am", "planning", "to", "study", "in", "Madrid", "next", "year"],
    accept: [["I", "am", "planning", "to", "study", "next", "year", "in", "Madrid"]],
    suffix: ".",
    distractors: ["plan", "studying"],
  },
  {
    id: 10,
    speaker: "How was your weekend?",
    prefix: "It was great.",
    answer: ["We", "went", "hiking", "and", "had", "a", "picnic", "by", "the", "lake"],
    suffix: ".",
    distractors: ["go", "hike"],
  },
];

export const emailTask = {
  recipient: "Ms. Whitfield",
  scenario:
    "You live in a university-owned apartment. Two weeks ago, the heating in your apartment stopped working. You submitted an online repair request the same day and sent a follow-up message last week, but you have not received any reply, and no one has come to fix the problem. The weather is getting colder. Write an email to Ms. Whitfield, the manager of university housing.",
  points: [
    "Explain the problem and what you have already done to report it.",
    "Describe how the problem is affecting you.",
    "Ask for a specific action and suggest how the repair process could be improved.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["heating", "heater", "heat", "broken", "not working", "stopped", "apartment", "repair request", "request", "submitted", "online", "follow-up", "followed up", "message", "reported", "two weeks", "no reply", "no response", "nobody", "no one"],
    ["cold", "freezing", "temperature", "sleep", "sick", "ill", "health", "study", "concentrate", "uncomfortable", "difficult", "affect", "affected", "impact", "worried", "frustrated", "blanket", "blankets", "jacket"],
    ["send", "technician", "fix", "repair", "as soon as possible", "immediately", "this week", "heater", "portable", "temporary", "request", "ask", "would like", "please", "improve", "in the future", "future", "confirmation", "update", "notify", "tracking", "phone", "suggest", "recommend"],
  ],
  formal: true,
  minWords: 100,
  lengthSteps: [[140, 5], [120, 4.5], [100, 4], [80, 3], [60, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "public health",
  professor: "Dr. Castillo",
  question:
    "Research shows that teenagers naturally fall asleep later at night than adults, and many are tired during early morning classes. Some school districts have responded by moving the start of the high school day from around 7:30 a.m. to 8:30 a.m. or later. Should high schools start later in the morning? In your post, also identify a group of people for whom a later start time could cause problems, and explain what support they should receive.",
  posts: [
    {
      name: "Grace",
      text: "Yes, I think schools should start later. When I was in high school, I could barely stay awake in my first class. Students who get more sleep are more focused, get better grades, and are even safer when they drive to school.",
    },
    {
      name: "Omar",
      text: "I understand the research, but a later start means school ends later too. That leaves less time for sports, part-time jobs, and homework in the evening. Some students might just stay up even later and end up with no more sleep than before.",
    },
  ],
  topicKeywords: [
    "school", "schools", "high school", "start", "start time", "later", "earlier", "morning", "sleep", "tired", "rest",
    "teenagers", "teens", "students", "student", "classes", "class", "focus", "grades", "health", "schedule", "afternoon",
    "sports", "jobs", "homework", "parents", "families", "bus", "buses", "transportation", "evening",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on later start times for high schools",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "i agree", "i disagree", "should start later", "should not start later", "shouldn't start later", "later start", "support", "oppose", "in favor", "against"],
    },
    {
      label: "Identify a group a later start could cause problems for and say what support they should receive",
      keywords: ["problem", "problems", "difficult", "harder", "working parents", "parents", "families", "athletes", "sports", "part-time job", "part-time jobs", "younger siblings", "bus drivers", "teachers", "childcare", "support", "receive", "instead", "before-school", "early drop-off", "supervision", "program", "programs", "flexible", "transportation", "bus"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
